package com.wharpe.finsimweb.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import org.jetbrains.compose.web.css.LineStyle
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import com.wharpe.finsimweb.FontMono
import com.wharpe.finsimweb.HeadlineTextStyle
import com.wharpe.finsimweb.MoneyStyle
import com.wharpe.finsimweb.SubheadlineTextStyle
import com.wharpe.finsimweb.components.layouts.PageLayoutData
import com.wharpe.finsimweb.toSitePalette

val SectionStyle = CssStyle {
    base { Modifier.fillMaxWidth().maxWidth(56.cssRem).gap(1.cssRem).margin(topBottom = 3.cssRem) }
    Breakpoint.MD { Modifier.margin(topBottom = 5.cssRem) }
}

val EyebrowStyle = CssStyle.base {
    Modifier
        .fontSize(0.75.cssRem)
        .letterSpacing(0.08.cssRem)
        .fontWeight(FontWeight.SemiBold)
        .color(colorMode.toSitePalette().muted)
}

val HeroLogoStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(26.cssRem)
            .borderRadius(1.25.cssRem)
            .display(DisplayStyle.Block)
            .margin(bottom = 0.5.cssRem)
    }
    Breakpoint.MD { Modifier.maxWidth(34.cssRem) }
}

val BadgeStyle = CssStyle.base {
    Modifier
        .backgroundColor(colorMode.toSitePalette().brand.primary)
        .color(Colors.White)
        .borderRadius(0.75.cssRem)
        .padding(topBottom = 0.7.cssRem, leftRight = 1.2.cssRem)
        .fontSize(0.9.cssRem)
        .fontWeight(FontWeight.SemiBold)
}

val SectionTitleStyle = CssStyle {
    base { Modifier.fontSize(1.375.cssRem).lineHeight(1.27).fontWeight(FontWeight.SemiBold) }
    Breakpoint.MD {
        Modifier.fontSize(1.875.cssRem).lineHeight(1.27).fontWeight(FontWeight.Bold).letterSpacing((-0.5).px)
    }
}

val CardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .backgroundColor(colorMode.toSitePalette().surface)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().outline)
            .borderRadius(1.25.cssRem)
            .padding(1.2.cssRem)
            .gap(0.5.cssRem)
    }
    Breakpoint.MD { Modifier.padding(1.6.cssRem) }
}

val CardGridStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(1.cssRem)
            .display(DisplayStyle.Grid)
            .gridTemplateColumns { size(1.fr) }
    }
}

val ThreeColumnGridStyle = CssStyle {
    base { Modifier.gridTemplateColumns { size(1.fr) } }
    Breakpoint.MD { Modifier.gridTemplateColumns { repeat(3) { size(1.fr) } } }
}

val TwoColumnGridStyle = CssStyle {
    base { Modifier.gridTemplateColumns { size(1.fr) } }
    Breakpoint.MD { Modifier.gridTemplateColumns { repeat(2) { size(1.fr) } } }
}

val StepNumberStyle = CssStyle.base {
    Modifier
        .fontFamily(FontMono, "ui-monospace", "Menlo", "monospace")
        .fontSize(0.85.cssRem)
        .letterSpacing(0.05.cssRem)
        .fontWeight(FontWeight.Bold)
        .color(colorMode.toSitePalette().brand.primary)
}

val DisclaimerStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .maxWidth(56.cssRem)
        .backgroundColor(colorMode.toSitePalette().surfaceVariant)
        .borderRadius(1.25.cssRem)
        .padding(1.2.cssRem)
        .fontSize(0.9.cssRem)
        .color(colorMode.toSitePalette().muted)
        .margin(topBottom = 2.cssRem)
}

@Composable
private fun Card(title: String, body: String, eyebrow: String? = null, money: String? = null) {
    Column(CardStyle.toModifier()) {
        if (eyebrow != null) {
            Div(StepNumberStyle.toAttrs()) { SpanText(eyebrow) }
        }
        Div(Modifier.fontSize(1.cssRem).fontWeight(FontWeight.SemiBold).toAttrs()) {
            SpanText(title)
            if (money != null) {
                SpanText(" ")
                SpanText(money, MoneyStyle.toModifier())
            }
        }
        SpanText(body, Modifier.color(ColorMode.current.toSitePalette().muted).fontSize(0.875.cssRem))
    }
}

@Composable
private fun Section(eyebrow: String, title: String, content: @Composable () -> Unit) {
    Column(SectionStyle.toModifier()) {
        Div(EyebrowStyle.toAttrs()) { SpanText(eyebrow.uppercase()) }
        Div(SectionTitleStyle.toAttrs()) { SpanText(title) }
        content()
    }
}

@InitRoute
fun initHomePage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("FinSim — apprends à investir sans risquer un franc"))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun HomePage() {
    val palette = ColorMode.current.toSitePalette()

    Column(
        Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {

        Column(SectionStyle.toModifier().margin(top = 2.cssRem)) {
            Image("/img/logo-landscape.webp", "FinSim", HeroLogoStyle.toModifier())
            Div(HeadlineTextStyle.toAttrs()) {
                SpanText("Apprends à investir ")
                SpanText("sans risquer un franc", Modifier.color(palette.brand.primary))
            }
            Div(SubheadlineTextStyle.toAttrs()) {
                SpanText(
                    "FinSim est un simulateur de bourse. Tu démarres avec 10 000 $ fictifs et tu " +
                        "achètes de vrais actifs à leurs vrais cours. L'argent est faux, les prix " +
                        "sont réels, les leçons aussi."
                )
            }
            Row(Modifier.gap(0.75.cssRem).margin(top = 0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                Div(BadgeStyle.toAttrs()) { SpanText("Bientôt disponible") }
                SpanText(
                    "L'application mobile arrive.",
                    Modifier.color(palette.muted).fontSize(0.9.cssRem)
                )
            }
        }

        Section("Comment ça marche", "Trois étapes, aucun risque") {
            Div(CardGridStyle.toModifier().then(ThreeColumnGridStyle.toModifier()).toAttrs()) {
                Card(
                    eyebrow = "01",
                    title = "Tu reçois",
                    money = "10 000 \$",
                    body = "Fictifs, crédités à l'inscription. Personne ne te demande un franc."
                )
                Card(
                    eyebrow = "02",
                    title = "Tu passes des ordres",
                    body = "Achat et vente sur des actifs réels, aux cours du marché rafraîchis en continu."
                )
                Card(
                    eyebrow = "03",
                    title = "Tu vois tes résultats",
                    body = "Gains et pertes calculés sur chaque position, comme sur un vrai compte."
                )
            }
        }

        Section("Pour qui", "Pensé pour le marché africain") {
            Div(CardGridStyle.toModifier().then(TwoColumnGridStyle.toModifier()).toAttrs()) {
                Card(
                    title = "Tu n'as jamais investi",
                    body = "Comprendre ce qu'est un ordre, un portefeuille ou une plus-value en le faisant, " +
                        "plutôt qu'en lisant une définition."
                )
                Card(
                    title = "Tu hésites à te lancer",
                    body = "Teste une stratégie pendant des semaines avant d'y mettre le moindre franc CFA."
                )
                Card(
                    title = "Tu veux t'entraîner",
                    body = "Le simulateur ne juge pas. Perdre 3 000 \$ fictifs coûte une leçon, pas un salaire."
                )
                Card(
                    title = "Tu enseignes",
                    body = "Un support concret pour parler d'épargne et de marchés à un groupe."
                )
            }
        }

        Div(DisclaimerStyle.toAttrs()) {
            SpanText(
                "FinSim est un outil pédagogique. Les portefeuilles sont fictifs et aucune " +
                    "transaction réelle n'est effectuée. Rien sur ce site ne constitue un conseil " +
                    "en investissement. Les performances passées d'un actif ne préjugent pas de " +
                    "ses performances futures."
            )
        }
    }
}
